import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import TextField from '@app/src/components/Ui/TextField'
import type { AnswerFlagFormData } from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import { ANSWER_FLAG_FORM_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import type { Question } from '@app/src/types/resourceExplorer'
import { AnswerRuleType, AnswerStatus } from '@app/src/types/resourceExplorer'
import { QuestionTypes } from '@app/src/wf-constants'
import { Box, FormControlLabel, InputAdornment, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import NumberFormat from 'react-number-format'

// Styled component to remove left margin from FormControlLabel, which is added by default in MUI when used inside a RadioGroup
const StyledFormControlLabel = styled(FormControlLabel)({
  marginLeft: 0,
})

enum TriggerType {
  SpecificAnswers = 'specificAnswers',
  AnswerStatus = 'answerStatus',
}

const isSpecificAnswersAvailable = (questionTypeName?: string): boolean => {
  if (!questionTypeName) return false
  return questionTypeName === QuestionTypes.Options || questionTypeName === QuestionTypes.Number
}

const getInitialTriggerType = (ruleType?: AnswerRuleType) => {
  if (!ruleType) return undefined
  return ruleType === AnswerRuleType.AnswerStatus ? TriggerType.AnswerStatus : TriggerType.SpecificAnswers
}

const getRuleTypeForTrigger = (triggerType: TriggerType, questionTypeName?: string): AnswerRuleType | undefined => {
  if (triggerType === TriggerType.AnswerStatus) return AnswerRuleType.AnswerStatus
  if (triggerType === TriggerType.SpecificAnswers && questionTypeName === QuestionTypes.Options)
    return AnswerRuleType.SelectedValue
  return undefined // Number questions: ruleType set when user picks above/below/equal
}

const AnswerFlagTrigger: React.FC = () => {
  const { formatMessage } = useIntl()
  const { control, watch, setValue, getValues } = useFormContext<AnswerFlagFormData>()
  const [triggerType, setTriggerType] = useState<TriggerType | undefined>(
    getInitialTriggerType(getValues(ANSWER_FLAG_FORM_KEYS.RULE_TYPE)),
  )
  const isInitialMount = useRef(true)

  const questionId = watch(ANSWER_FLAG_FORM_KEYS.QUESTION_ID)
  const question = watch(ANSWER_FLAG_FORM_KEYS.QUESTION)
  const questionTypeName = question?.questionType?.name

  const { items: questions, isLoading: isLoadingQuestions } = useFetchCollectionWithPost<Question>({
    key: FetchKey.Questions,
    endpoint: endpoints.questionCollection,
    payload: {
      include: ['requestCollection.items', 'unit', 'questionType'],
      filter: [],
    },
  })

  useEffect(() => {
    const selectedQuestion = questionId ? questions?.find(q => q.id === questionId) : undefined
    setValue(ANSWER_FLAG_FORM_KEYS.QUESTION, selectedQuestion)
  }, [questionId, questions, setValue])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // User changed question - reset trigger type and rule values
    const selectedQuestion = questionId ? questions?.find(q => q.id === questionId) : undefined
    const canUseSpecificAnswers = isSpecificAnswersAvailable(selectedQuestion?.questionType?.name)

    if (!canUseSpecificAnswers && Boolean(questionId)) {
      // Only AnswerStatus available - set to empty array (multi-select)
      setTriggerType(TriggerType.AnswerStatus)
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_TYPE, AnswerRuleType.AnswerStatus)
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_VALUE, [])
    } else {
      // Reset to no trigger type selected
      setTriggerType(undefined)
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_TYPE, undefined)
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_VALUE, undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId])

  const handleTriggerTypeChange = (newTriggerType: TriggerType) => {
    setTriggerType(newTriggerType)
    const newRuleType = getRuleTypeForTrigger(newTriggerType, questionTypeName)

    // Clear ruleValue appropriately based on the new rule type
    // AnswerStatus and SelectedValue (Options) use multi-select, so set to empty array
    // Number types and undefined rule types should be set to undefined
    const shouldUseArray = newRuleType === AnswerRuleType.AnswerStatus || newRuleType === AnswerRuleType.SelectedValue

    setValue(ANSWER_FLAG_FORM_KEYS.RULE_VALUE, shouldUseArray ? [] : undefined)
    setValue(ANSWER_FLAG_FORM_KEYS.RULE_TYPE, newRuleType)
  }

  const canUseSpecificAnswers = isSpecificAnswersAvailable(questionTypeName)

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {formatMessage({ id: 'answerFlagRules.creation.trigger.selectQuestion' })}
        </Typography>
        <Select<number>
          name={ANSWER_FLAG_FORM_KEYS.QUESTION_ID}
          fieldLabel={formatMessage({ id: 'answerFlagRules.creation.trigger.selectQuestionPlaceholder' })}
          control={control}
          required
          options={questions.map(q => ({
            label: q.questionText,
            value: q.id,
          }))}
          getOptionKey={option => option.value}
          isLoading={isLoadingQuestions}
        />
      </Box>

      {questionId && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {formatMessage({ id: 'answerFlagRules.creation.trigger.triggerType' })}
          </Typography>
          <RadioGroup value={triggerType ?? ''} onChange={(_, value) => handleTriggerTypeChange(value as TriggerType)}>
            <StyledFormControlLabel
              value={TriggerType.SpecificAnswers}
              control={<Radio />}
              disabled={!canUseSpecificAnswers}
              label={
                <Typography color={!canUseSpecificAnswers ? 'text.disabled' : 'text.primary'}>
                  <strong>{formatMessage({ id: 'answerFlagRules.creation.trigger.specificAnswers' })}</strong> (
                  {formatMessage({ id: 'answerFlagRules.creation.trigger.specificAnswersDescription' })})
                </Typography>
              }
            />

            {triggerType === TriggerType.SpecificAnswers && canUseSpecificAnswers && (
              <Box ml={4} mt={1} mb={1}>
                <SpecificAnswersInput question={question} />
              </Box>
            )}

            <StyledFormControlLabel
              value={TriggerType.AnswerStatus}
              control={<Radio />}
              label={
                <Typography>
                  <strong>{formatMessage({ id: 'answerFlagRules.creation.trigger.answerStatus' })}</strong> (
                  {formatMessage({ id: 'answerFlagRules.creation.trigger.answerStatusDescription' })})
                </Typography>
              }
            />

            {triggerType === TriggerType.AnswerStatus && (
              <Box ml={4} mt={1}>
                <AnswerStatusInput />
              </Box>
            )}
          </RadioGroup>
        </Box>
      )}
    </Stack>
  )
}

const SpecificAnswersInput: React.FC<{ question?: Question }> = ({ question }) => {
  const questionTypeName = question?.questionType?.name

  switch (questionTypeName) {
    case QuestionTypes.Options:
      return <OptionsInput question={question} />
    case QuestionTypes.Number:
      return <NumberInputs question={question} />
    default:
      return null
  }
}

const OptionsInput: React.FC<{ question?: Question }> = ({ question }) => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<AnswerFlagFormData>()
  const collectionItems = question?.requestCollection?.items ?? []

  return (
    <Stack spacing={1}>
      <Select<string>
        name={ANSWER_FLAG_FORM_KEYS.RULE_VALUE}
        fieldLabel={formatMessage({ id: 'answerFlagRules.creation.trigger.selectOptions' })}
        control={control}
        multiple
        options={collectionItems.map(item => ({
          label: item.displayText,
          value: item.value,
        }))}
      />
      <Typography variant="body2" color="text.secondary">
        {formatMessage({ id: 'answerFlagRules.creation.trigger.selectOptionsHelperText' })}
      </Typography>
    </Stack>
  )
}

const NumberInputs: React.FC<{ question?: Question }> = ({ question }) => {
  const { formatMessage } = useIntl()
  const { watch, setValue } = useFormContext<AnswerFlagFormData>()
  const unitSymbol = question?.unit?.symbol ?? question?.unit?.name ?? ''

  const handleNumberChange = (ruleType: AnswerRuleType, value: string) => {
    if (!value) {
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_TYPE, undefined)
      setValue(ANSWER_FLAG_FORM_KEYS.RULE_VALUE, undefined)
      return
    }
    setValue(ANSWER_FLAG_FORM_KEYS.RULE_TYPE, ruleType)
    setValue(ANSWER_FLAG_FORM_KEYS.RULE_VALUE, value)
  }

  const currentRuleType = watch(ANSWER_FLAG_FORM_KEYS.RULE_TYPE)
  const currentRuleValue = watch(ANSWER_FLAG_FORM_KEYS.RULE_VALUE)

  return (
    <Stack spacing={2}>
      <NumberField
        label={formatMessage({ id: 'answerFlagRules.creation.trigger.above' })}
        unit={unitSymbol}
        value={
          currentRuleType === AnswerRuleType.NumberGreaterThan && typeof currentRuleValue === 'string'
            ? (currentRuleValue ?? '')
            : ''
        }
        onChange={value => handleNumberChange(AnswerRuleType.NumberGreaterThan, value)}
        disabled={Boolean(currentRuleType) && currentRuleType !== AnswerRuleType.NumberGreaterThan}
      />
      <NumberField
        label={formatMessage({ id: 'answerFlagRules.creation.trigger.below' })}
        unit={unitSymbol}
        value={
          currentRuleType === AnswerRuleType.NumberLowerThan && typeof currentRuleValue === 'string'
            ? (currentRuleValue ?? '')
            : ''
        }
        onChange={value => handleNumberChange(AnswerRuleType.NumberLowerThan, value)}
        disabled={Boolean(currentRuleType) && currentRuleType !== AnswerRuleType.NumberLowerThan}
      />
      <NumberField
        label={formatMessage({ id: 'answerFlagRules.creation.trigger.equal' })}
        unit={unitSymbol}
        value={
          currentRuleType === AnswerRuleType.NumberEqualTo && typeof currentRuleValue === 'string'
            ? (currentRuleValue ?? '')
            : ''
        }
        onChange={value => handleNumberChange(AnswerRuleType.NumberEqualTo, value)}
        disabled={Boolean(currentRuleType) && currentRuleType !== AnswerRuleType.NumberEqualTo}
      />
    </Stack>
  )
}

interface NumberFieldProps {
  label: string
  unit: string
  value: string
  onChange: (value: string) => void
  disabled: boolean
}

const NumberField: React.FC<NumberFieldProps> = ({ label, unit, value, onChange, disabled }) => {
  const { formatMessage } = useIntl()
  return (
    <Box>
      <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'} gutterBottom>
        {label}
      </Typography>
      <NumberFormat
        customInput={TextField}
        fullWidth
        placeholder={formatMessage({ id: 'answerFlagRules.creation.trigger.value' })}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        disabled={disabled}
        size="small"
        InputProps={{
          endAdornment: unit ? <InputAdornment position="end">{unit}</InputAdornment> : undefined,
        }}
        sx={{ backgroundColor: 'grey.100' }}
      />
    </Box>
  )
}

const SELECTABLE_ANSWER_STATUSES = [
  AnswerStatus.Answered,
  AnswerStatus.NotApplicable,
  AnswerStatus.NotAvailable,
  AnswerStatus.Other,
]

const AnswerStatusInput: React.FC = () => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<AnswerFlagFormData>()

  const answerStatusOptions = SELECTABLE_ANSWER_STATUSES.map(status => ({
    label: formatMessage({ id: `templateBuilder.answerClassifications.classificationsValues.${status}` }),
    value: status,
  }))

  return (
    <Stack spacing={1}>
      <Select<string>
        name={ANSWER_FLAG_FORM_KEYS.RULE_VALUE}
        fieldLabel={formatMessage({ id: 'answerFlagRules.creation.trigger.selectAnswerStatus' })}
        control={control}
        options={answerStatusOptions}
        multiple
      />
      <Typography variant="body2" color="text.secondary">
        {formatMessage({ id: 'answerFlagRules.creation.trigger.selectOptionsHelperText' })}
      </Typography>
    </Stack>
  )
}

export default AnswerFlagTrigger
